import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FeatureType, TimeoutError } from '@as/api';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root'
})
export class ErrorMessageService {
  constructor(private translateService: TranslateService) {}

  getTranslatedErrorMessage(error: Error | HttpErrorResponse, defaultMessage?: string) {
    // No Internet connection
    if (!navigator.onLine) {
      return this.translateService.instant('APPLICATION.ERRORS.noConnection');
    }

    if (error instanceof TimeoutError) {
      return this.translateService.instant(`API.ERRORS.${error.message}`);
    }

    if (this.isClientError(error)) {
      return this.handleClientError(error);
    }

    if (defaultMessage) {
      return this.translateService.instant(defaultMessage);
    }

    return 'Unhandled error';
  }

  private handleClientError(error: HttpErrorResponse): string {
    if (Array.isArray(error.error)) {
      return error.error
        .map(err => {
          return this.handleAsError(err);
        })
        .join('\n');
    }

    return this.handleAsError(error);
  }

  private handleAsError(
    error: HttpErrorResponse | { code: string; message: string; Message: string; details?: Record<string, unknown> }
  ): string {
    const errorData = error instanceof HttpErrorResponse ? error.error : error;
    if (errorData && errorData.code) {
      if (Object.values(FeatureType).some(featureType => featureType === errorData.code)) {
        const licensingTranslationKey = `API.LICENSING_ERRORS.${errorData.code}`;
        return this.translateService.instant(licensingTranslationKey, errorData.details);
      }

      const errorTranslationKey = `API.ERRORS.${errorData.code}`;
      const translatedError = this.translateService.instant(errorTranslationKey);
      if (errorTranslationKey !== translatedError) {
        return translatedError;
      }
    }

    return errorData?.message ?? errorData?.Message;
  }

  private isClientError(error: Error): error is HttpErrorResponse {
    return error instanceof HttpErrorResponse && error.status >= 400 && error.status < 500;
  }
}
